import React, { Component } from "react";
import { Alert, Modal, Tag, message } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import axios from "axios";
import { connect } from "react-redux";
import { ModalItem, ButtonItem, CheckItem, SelectEmpleadosItem, fmtFechaDia } from "../../../../Components/Items";

export const COLOR_COINCIDENCIA = { 3: "green", 2: "orange", 1: "orange", 0: "red" };

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

export const textoBiometria = (b) => {
  if (!b) return "Sin huellas ni rostro";
  const partes = [];
  if (b.huellas) partes.push(plural(b.huellas, "huella", "huellas"));
  if (b.rostros) partes.push(plural(b.rostros, "rostro", "rostros"));
  if (b.password) partes.push("contraseña");
  return partes.length ? partes.join(" · ") : "Sin huellas ni rostro";
};

const codigoTexto = (codigo) => (codigo !== null && codigo !== undefined ? String(codigo).padStart(4, "0") : "—");

/**
 * Vista previa y confirmación de la unificación de UN par de empleados duplicados: el mismo
 * trabajador dado de alta para el reloj checador y otra vez al cargar la nómina
 * (erp_backend/Controllers/unificarEmpleados.controllers.js).
 *
 * Al duplicado se le pasa al otro todo lo del reloj (asignaciones con el MISMO PIN, checadas,
 * turnos, huellas y rostros) y queda deshabilitado; la nómina no se mueve. Los candados viven en
 * el backend (`evaluarUnificacion`) y aquí sólo se pintan: se vuelve a evaluar cada vez que
 * cambia alguno de los dos empleados.
 *
 * Se abre desde la pantalla de duplicados detectados (con la pareja ya puesta) o desde el botón
 * "Unificar con…" del renglón (con uno solo puesto).
 */
class ModalUnificarEmpleado extends Component {
  state = {
    duplicadoId: null,
    conservarId: null,
    datos: null,
    error: null,
    loading: false,
    enviando: false,
    confirmado: false,
  };
  // Descarta las respuestas de una evaluación vieja: el usuario puede cambiar de empleado dos
  // veces antes de que regrese la primera.
  consultaVigente = 0;

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      const duplicadoId = this.props.duplicadoId || null;
      const conservarId = this.props.conservarId || null;
      this.setState({ duplicadoId, conservarId, datos: null, error: null, confirmado: false }, () => this.evaluar());
    }
  }

  async evaluar() {
    const { duplicadoId, conservarId } = this.state;
    const consulta = ++this.consultaVigente;
    if (!duplicadoId || !conservarId) {
      this.setState({ datos: null, error: null, loading: false });
      return;
    }
    this.setState({ loading: true, confirmado: false });
    try {
      const res = await axios.post(
        "empleados/evaluarUnificacion",
        { empresaId: this.props.empresaId, duplicadoId, conservarId },
        { headers: this.props.headersToken }
      );
      if (consulta !== this.consultaVigente) return;
      if (res.data.success !== 2) {
        this.setState({ datos: null, error: res.data.mensaje || "No se pudo evaluar", loading: false });
        return;
      }
      this.setState({ datos: res.data, error: null, loading: false });
    } catch (e) {
      if (consulta !== this.consultaVigente) return;
      this.setState({ loading: false, error: "Error de conexión al evaluar la unificación" });
    }
  }

  cambiar(campo, valor) {
    this.setState({ [campo]: valor || null }, () => this.evaluar());
  }

  intercambiar() {
    this.setState(
      (s) => ({ duplicadoId: s.conservarId, conservarId: s.duplicadoId }),
      () => this.evaluar()
    );
  }

  cerrar(unificado = false) {
    if (this.state.enviando) return;
    this.props.onCerrar(unificado);
  }

  unificar = async () => {
    const { duplicadoId, conservarId, confirmado } = this.state;
    this.setState({ enviando: true });
    try {
      const res = await axios.post(
        "empleados/unificarEmpleados",
        { empresaId: this.props.empresaId, duplicadoId, conservarId, confirmado },
        { headers: this.props.headersToken }
      );
      this.setState({ enviando: false });
      if (res.data.success !== 2) {
        message.error(res.data.mensaje || "No se pudo unificar", 8);
        this.evaluar();
        return;
      }
      message.success(res.data.mensaje || "Empleados unificados");
      const r = res.data.resumen || {};
      const advertencias = res.data.advertencias || [];
      Modal.info({
        title: "Unificación aplicada",
        width: 560,
        content: (
          <div>
            <p style={{ marginBottom: 4 }}>
              Pasaron a <b>{r.nombreConservar}</b>:
            </p>
            <ul>
              <li>{plural(r.asignaciones || 0, "asignación de reloj", "asignaciones de reloj")} (mismo PIN)</li>
              <li>
                {plural(r.checadas || 0, "checada", "checadas")}
                {r.checadasRepetidas ? ` (${r.checadasRepetidas} repetida(s) descartada(s))` : ""}
              </li>
              <li>{plural(r.turnos || 0, "turno", "turnos")}</li>
              <li>Biometría: {textoBiometria({
                huellas: r.biometria?.huellas,
                rostros: (r.biometria?.rostros || 0) + (r.biometria?.rostro ? 1 : 0),
                password: r.biometria?.password,
              })}</li>
              {r.codigo !== null && r.codigo !== undefined && <li>Código {codigoTexto(r.codigo)}</li>}
            </ul>
            <p style={{ marginBottom: 4 }}>
              <b>{r.nombreDuplicado}</b> quedó deshabilitado.
            </p>
            {advertencias.length > 0 && (
              <>
                <p style={{ marginBottom: 4 }}>Revisa:</p>
                <ul>{advertencias.map((a, i) => <li key={i}>{a}</li>)}</ul>
              </>
            )}
          </div>
        ),
      });
      this.props.onCerrar(true);
    } catch (e) {
      this.setState({ enviando: false });
      message.error("Error de conexión al unificar");
    }
  };

  renderTarjeta(emp, rol) {
    if (!emp) return null;
    return (
      <div
        style={{
          background: rol === "duplicado" ? "#fff1f0" : "#f6ffed",
          border: `1px solid ${rol === "duplicado" ? "#ffccc7" : "#b7eb8f"}`,
          borderRadius: 4, padding: "8px 12px", marginTop: 6, fontSize: 12, lineHeight: 1.7,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.nombre}</div>
        <div>
          Código <b>{codigoTexto(emp.codigo)}</b> · RFC <b>{emp.rfc || "—"}</b>
        </div>
        {(emp.curp || emp.numSeguridadSocial) && (
          <div style={{ color: "#666" }}>
            {emp.curp ? `CURP ${emp.curp}` : ""}{emp.curp && emp.numSeguridadSocial ? " · " : ""}
            {emp.numSeguridadSocial ? `NSS ${emp.numSeguridadSocial}` : ""}
          </div>
        )}
        <div>
          {emp.estado === 1 ? <Tag color="green">Activo</Tag> : <Tag>Deshabilitado</Tag>}
          {emp.fechaBaja && <Tag color="orange">Baja {fmtFechaDia(emp.fechaBaja)}</Tag>}
          {emp.acumulados > 0 && <Tag color="blue">Con historial de nómina</Tag>}
        </div>
        <div>
          Reloj:{" "}
          {emp.relojes?.length
            ? emp.relojes.map((r, i) => <Tag key={i} color="cyan">{r.reloj} · PIN {r.pin}</Tag>)
            : <span style={{ color: "#999" }}>sin asignación</span>}
        </div>
        <div>Biometría: {textoBiometria(emp.biometria)}</div>
        <div>Checadas: <b>{(emp.checadas || 0).toLocaleString("es-MX")}</b></div>
      </div>
    );
  }

  renderSeMueve() {
    const { datos } = this.state;
    const m = datos?.seMueve;
    if (!m) return null;
    const b = m.biometria || {};
    const nombreConservar = datos.empleados?.conservar?.nombre;
    return (
      <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 4, padding: "8px 12px", marginBottom: 10, fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Se va a pasar a {nombreConservar}:</div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li>
            {plural(m.asignaciones.length, "asignación de reloj", "asignaciones de reloj")}
            {m.asignaciones.length > 0 && (
              <span>
                {" "}
                {m.asignaciones.map((a, i) => (
                  <Tag key={i} color={a.activa ? "cyan" : "default"} style={{ marginBottom: 2 }}>
                    {a.reloj} · PIN {a.pin}{a.activa ? "" : " (inactiva)"}
                  </Tag>
                ))}
              </span>
            )}
          </li>
          <li>
            {plural(m.checadas, "checada", "checadas")}
            {m.checadasRepetidas ? ` (${m.checadasRepetidas} ya las tiene: se descartan)` : ""}
          </li>
          <li>
            {plural(m.turnos, "turno", "turnos")}
            {m.turnosQuedan ? ` (${m.turnosQuedan} se quedan por choque)` : ""}
          </li>
          <li>
            Biometría: {textoBiometria({ huellas: b.huellas, rostros: (b.rostros || 0) + (b.rostro ? 1 : 0), password: b.password })}
          </li>
          {m.codigo !== null && m.codigo !== undefined && <li>Código {codigoTexto(m.codigo)} (no tiene uno propio)</li>}
        </ul>
        <div style={{ color: "#888", marginTop: 4 }}>
          No se manda nada al reloj: el PIN es el mismo, así que conserva las huellas y el rostro.
          La nómina (prenóminas, recibos, finiquito) no se mueve.
        </div>
      </div>
    );
  }

  render() {
    const { datos, error, loading, enviando, confirmado, duplicadoId, conservarId } = this.state;
    const bloqueos = datos?.bloqueos || [];
    const avisos = datos?.avisos || [];
    const coincidencia = datos?.coincidencia;
    const requiereConfirmacion = !!datos?.requiereConfirmacion;
    const puedeUnificar = !loading && !!datos && !bloqueos.length && (!requiereConfirmacion || confirmado);

    return (
      <ModalItem
        title="Unificar empleados duplicados"
        visible={this.props.visible}
        spinning={enviando || loading}
        onCancel={() => this.cerrar(false)}
        paddingTop
        width={window.innerWidth > 1000 ? 860 : "95%"}
        destroyOnClose
      >
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 10 }}>
          <div style={{ flex: "1 1 300px", minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "#cf1322", marginBottom: 4 }}>Duplicado (se deshabilita)</div>
            <SelectEmpleadosItem
              empresaId={this.props.empresaId}
              headersToken={this.props.headersToken}
              value={duplicadoId || undefined}
              excluirId={conservarId || undefined}
              incluirBajas
              width="100%"
              placeholder="Empleado duplicado"
              onChange={(valor) => this.cambiar("duplicadoId", valor)}
            />
            {this.renderTarjeta(datos?.empleados?.duplicado, "duplicado")}
          </div>
          <div style={{ alignSelf: "center" }}>
            <ButtonItem
              htmlType="button"
              blanco
              size="small"
              icon={<SwapOutlined />}
              disabled={!duplicadoId && !conservarId}
              onClick={() => this.intercambiar()}
            >
              Intercambiar
            </ButtonItem>
          </div>
          <div style={{ flex: "1 1 300px", minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "#389e0d", marginBottom: 4 }}>Se queda (recibe lo del reloj)</div>
            <SelectEmpleadosItem
              empresaId={this.props.empresaId}
              headersToken={this.props.headersToken}
              value={conservarId || undefined}
              excluirId={duplicadoId || undefined}
              // También con bajas: tras "Intercambiar" puede quedar aquí uno deshabilitado y sin
              // esto el select pintaría su _id. Que el que se queda esté activo lo exige el backend.
              incluirBajas
              width="100%"
              placeholder="Empleado que se queda"
              onChange={(valor) => this.cambiar("conservarId", valor)}
            />
            {this.renderTarjeta(datos?.empleados?.conservar, "conservar")}
          </div>
        </div>

        {coincidencia && (
          <div style={{ marginBottom: 10 }}>
            Nombre: <Tag color={COLOR_COINCIDENCIA[coincidencia.puntaje]}>{coincidencia.texto}</Tag>
          </div>
        )}

        {error && <Alert type="error" showIcon style={{ marginBottom: 10 }} message={error} />}
        {bloqueos.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 10 }}
            message="No se puede unificar"
            description={<ul style={{ margin: 0, paddingLeft: 18 }}>{bloqueos.map((b, i) => <li key={i}>{b}</li>)}</ul>}
          />
        )}
        {avisos.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10 }}
            message="Antes de unificar"
            description={<ul style={{ margin: 0, paddingLeft: 18 }}>{avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>}
          />
        )}

        {!bloqueos.length && this.renderSeMueve()}

        {requiereConfirmacion && !bloqueos.length && (
          <div style={{ marginBottom: 10 }}>
            <CheckItem checked={confirmado} onChange={(v) => this.setState({ confirmado: v })}>
              Los nombres no son idénticos ({coincidencia?.texto?.toLowerCase()}). Confirmo que son la misma persona.
            </CheckItem>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 10 }}>
          <ButtonItem htmlType="button" blanco onClick={() => this.cerrar(false)}>
            Cancelar
          </ButtonItem>
          <ButtonItem
            htmlType="button"
            type="primary"
            loading={enviando}
            disabled={!puedeUnificar}
            onClick={this.unificar}
          >
            Unificar
          </ButtonItem>
        </div>
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalUnificarEmpleado);
